import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface ProfileData {
  headline: string;
  summary: string;
  experienceDescriptions: string[];
  skillRecommendations: string[];
  visibilityTips: string[];
}

const PersonalProfileTab: React.FC = () => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentTitle, setCurrentTitle] = useState('');
  const [experience, setExperience] = useState<string[]>(['']);
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [goals, setGoals] = useState('');
  const [profileData, setProfileData] = useState<ProfileData | null>(null);

  const handleAddExperience = () => {
    setExperience([...experience, '']);
  };

  const handleExperienceChange = (index: number, value: string) => {
    const updatedExperience = [...experience];
    updatedExperience[index] = value;
    setExperience(updatedExperience);
  };

  const handleRemoveExperience = (index: number) => {
    if (experience.length > 1) {
      const updatedExperience = [...experience];
      updatedExperience.splice(index, 1);
      setExperience(updatedExperience);
    }
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !skills.includes(skillInput.trim())) {
      setSkills([...skills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setSkills(skills.filter(s => s !== skill));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const filteredExperience = experience.filter(exp => exp.trim() !== '');
    
    if (!currentTitle || filteredExperience.length === 0 || skills.length === 0 || !goals) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const userId = await getToken();
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/linkedin-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId,
            currentTitle, 
            experience: filteredExperience, 
            skills, 
            goals 
          }),
        }
      );
      
      const data = await response.json();
      setProfileData(data.profileData);
      toast.success('LinkedIn profile optimization complete!');
    } catch (error) {
      console.error('Error optimizing LinkedIn profile:', error);
      toast.error('Failed to optimize LinkedIn profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exampleData = {
    currentTitle: 'Marketing Manager',
    experience: ['Managed a team of 10 marketers at Acme Corp, 2019-2024. Led digital campaigns that increased lead generation by 40%.'],
    skills: ['Digital Marketing', 'SEO', 'Content Strategy'],
    goals: 'Become a CMO at a leading SaaS company, focusing on innovative brand growth.'
  };

  const handleUseExample = () => {
    setCurrentTitle(exampleData.currentTitle);
    setExperience(exampleData.experience);
    setSkills(exampleData.skills);
    setSkillInput('');
    setGoals(exampleData.goals);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Form */}
      <div className="rounded-xl bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
            Personal Profile Details
          </h2>
          <button
            type="button"
            className="text-xs px-3 py-1.5 bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 hover:bg-white transition-all duration-200 font-medium text-indigo-700"
            onClick={handleUseExample}
          >
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Try Example
            </span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="currentTitle" className="block text-sm font-semibold text-gray-700 mb-2">
              Current Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="currentTitle"
              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              value={currentTitle}
              onChange={(e) => setCurrentTitle(e.target.value)}
              placeholder="e.g. Marketing Manager, Software Developer"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Experience <span className="text-red-500">*</span>
            </label>
            {experience.map((exp, index) => (
              <div key={index} className="flex mb-2 last:mb-0">
                <textarea
                  className="flex-grow px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  value={exp}
                  onChange={(e) => handleExperienceChange(index, e.target.value)}
                  placeholder={`Experience ${index + 1}: Position, Company, Duration, Responsibilities`}
                  rows={2}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveExperience(index)}
                  className="bg-red-500 text-white px-3 rounded-r-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={experience.length <= 1}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddExperience}
              className="mt-2 inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200 hover:bg-white transition-all text-sm font-medium text-indigo-700"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Experience
            </button>
          </div>
          
          <div>
            <label htmlFor="skills" className="block text-sm font-semibold text-gray-700 mb-2">
              Skills
            </label>
            <div className="flex">
              <input
                type="text"
                id="skills"
                className="flex-grow px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g. Project Management, JavaScript"
              />
              <button
                type="button"
                onClick={handleAddSkill}
                className="bg-indigo-500 text-white px-4 py-2.5 rounded-r-lg hover:bg-indigo-600 transition-colors font-medium"
              >
                Add
              </button>
            </div>
            
            {skills.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200"
                  >
                    {skill}
                    <button
                      type="button"
                      className="ml-2 text-indigo-600 hover:text-red-500 transition-colors"
                      onClick={() => handleRemoveSkill(skill)}
                    >
                      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div>
            <label htmlFor="goals" className="block text-sm font-semibold text-gray-700 mb-2">
              Career Goals
            </label>
            <textarea
              id="goals"
              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              placeholder="Describe your career goals, aspirations, and what you're looking to achieve"
              rows={3}
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-6 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-0.5'}`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Optimizing Profile...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Optimize LinkedIn Profile
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            )}
          </button>
        </form>
      </div>

      {/* Results Panel */}
      <div>
        {profileData ? (
          <div className="rounded-xl bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm p-6">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-6">
              Optimized Profile
            </h2>
            
            <div className="space-y-5">
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-indigo-700 mb-2">Professional Headline</h3>
                <p className="text-gray-700 font-medium">{profileData.headline}</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-indigo-700 mb-2">Profile Summary</h3>
                <p className="text-gray-700 text-sm whitespace-pre-line">{profileData.summary}</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-indigo-700 mb-3">Experience Descriptions</h3>
                <div className="space-y-2">
                  {profileData.experienceDescriptions.map((desc, index) => (
                    <div key={index} className="bg-white/80 p-3 rounded-lg border border-gray-100">
                      <p className="text-gray-700 text-sm whitespace-pre-line">{desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-indigo-700 mb-3">Recommended Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {profileData.skillRecommendations.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-indigo-700 mb-3">Profile Visibility Tips</h3>
                <ul className="space-y-2">
                  {profileData.visibilityTips.map((tip, index) => (
                    <li key={index} className="flex items-start bg-white/80 p-2 rounded-lg">
                      <svg className="w-5 h-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700 text-sm">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-white/40 backdrop-blur-sm border border-gray-200 border-dashed p-12 text-center h-full flex flex-col items-center justify-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Profile Optimization Yet</h3>
            <p className="text-gray-600 text-sm">
              Fill out the form and click "Optimize LinkedIn Profile" to see your results here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalProfileTab;
